import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Exchange, exchangeFromJSON, exchangeToJSON } from '../proto/generated/common'
import { getSecuritySnapshot } from '../selectors/security'
import { useDispatch } from 'react-redux'
import { getSecurityAsync, getSecurityKey, isValidExchange } from '../util/security'
import { fetchSecuritySnapshotsBulk } from '../services/apis/candles'
import useSetPrimarySecurity from '../hooks/useSetPrimarySecurity'
import { Security } from '../proto/generated/security'
import { Paper } from '@mui/material'
import { simulatorSecurities } from '../assets/constants'

interface Props extends PropsWithChildren {
    simulator?: boolean
}

const FnoPageWrapper: React.FC<Props> = ({ children, simulator = false }) => {
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const dispatch = useDispatch()
    const { exchange: urlExchange, symbol: urlCode } = useParams()
    const exchange = exchangeFromJSON(urlExchange || '')
    const mounted = useRef(false)
    const previousSecurity = useRef<{ exchange: Exchange, code: string }>(null)
    const setPrimarySecurity = useSetPrimarySecurity()
    const [symbolNotFound, setSymbolNotFound] = useState(true)

    useEffect(() => {
        if (!urlExchange || !urlCode) {
            mounted.current = true
            navigate(`${pathname}/${exchangeToJSON(Exchange.NSE)}/NIFTY_50`, { replace: true })
        } else if (!isValidExchange(exchange)) {
            setSymbolNotFound(false)
        } else if (isValidExchange(exchange)) {
            const securityKey = getSecurityKey(exchange, urlCode || '')
            if (simulator) {
                const index = simulatorSecurities.find(sec => sec.code === urlCode)
                if (!index || index.exchange !== exchange) {
                    setSymbolNotFound(false)
                    return
                }
                getSecurityAsync(securityKey).then((security) => {
                    fetchSecuritySnapshotsBulk({
                        securities: [{
                            exchange,
                            code: security?.underlyingSecurityCode ?? ''
                        }],
                        dispatch
                    })
                    setPrimarySecurity(securityKey, [security], false)
                    mounted.current = true
                    previousSecurity.current = { exchange, code: urlCode ?? '' }
                })
            } else {
                getSecurityAsync(securityKey).then((security) => {
                    if (!security) {
                        setSymbolNotFound(false)
                        return
                    }
                    fetchSecuritySnapshotsBulk({
                        securities: [{
                            exchange,
                            code: security?.underlyingSecurityCode ?? ''
                        }],
                        dispatch
                    })
                    setPrimarySecurity(securityKey, [security], false)
                    mounted.current = true
                    previousSecurity.current = { exchange, code: urlCode ?? '' }
                })
            }
        }
    }, [urlExchange, urlCode, pathname])

    useEffect(() => {
        const { exchange: prevExchange, code: prevCode } = previousSecurity.current || {}
        if (!mounted.current || !urlExchange || !urlCode || (prevExchange === exchange && prevCode === urlCode)) return
        const securityKey = getSecurityKey(exchange, urlCode)
        const snapshot = getSecuritySnapshot(securityKey)

        if (!snapshot) {
            getSecurityAsync(securityKey).then((security) => {
                if (!security) {
                    setSymbolNotFound(false)
                    return
                }
                fetchSecuritySnapshotsBulk({
                    securities: [{
                        exchange,
                        code: urlCode
                    }, {
                        exchange,
                        code: security?.underlyingSecurityCode ?? ''
                    }],
                    dispatch
                }).then(({ securitySnapshots }) => {
                    if (securitySnapshots.length && security) {
                        setPrimarySecurity(securityKey, [security], false)
                    }
                })
            })
        } else {
            getSecurityAsync(securityKey).then((security) => {
                if (!security) {
                    setSymbolNotFound(false)
                    return
                }
                setPrimarySecurity(securityKey, [security], false)
            })
        }

        previousSecurity.current = { exchange, code: urlCode }
    }, [exchange, urlCode])
    return !symbolNotFound ? (
        <><Paper sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100%'
        }}>
            Symbol not found
        </Paper> </>
    ) : (
        <>{children}</>
    )
}

export default FnoPageWrapper